/**
 * Decimal number field for weights and volumes (kg, CBM, LDM ...).
 *
 * The decimal separator is always a dot: a comma typed by the user (Turkish keyboards and
 * browser locales use one) becomes a dot right away, other characters are refused and at most
 * `decimals` digits are kept after the dot. "3053,83" is entered as 3053.83 and printed on the
 * B/L as 3053.830 KGS.
 *
 * Drop-in for a TextField whose onChange reads e.target.value (also with Number(...)): the
 * field keeps its own text, so an unfinished value such as "12." is not lost while typing.
 */
import React, { forwardRef, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";

export const toDotDecimal = (raw, decimals = 3) => {
    let text = String(raw ?? "");
    // Turkish style "3.053,83": dots before a comma are thousands separators.
    const comma = text.lastIndexOf(",");
    if (comma !== -1 && text.lastIndexOf(".") < comma) text = text.replace(/\./g, "");
    text = text.replace(/,/g, ".").replace(/[^\d.]/g, "");
    const dot = text.indexOf(".");
    if (dot !== -1) {
        text = `${text.slice(0, dot + 1)}${text.slice(dot + 1).replace(/\./g, "")}`;
        const [whole, fraction] = text.split(".");
        text = `${whole}.${fraction.slice(0, decimals)}`;
    }
    return text;
};

const sameNumber = (a, b) => {
    if ((a === "" || a === null || a === undefined) && (b === "" || b === null || b === undefined)) return true;
    return Number(a) === Number(b);
};

const DecimalTextField = forwardRef(function DecimalTextField(
    { value, onChange, decimals = 3, inputProps, ...rest },
    ref
) {
    const [text, setText] = useState(value === null || value === undefined ? "" : String(value));

    // A value set from outside (loading a file, totals, reset) replaces the text unless it is
    // the same number the text already shows ("12." and 12).
    useEffect(() => {
        if (!sameNumber(value, text)) setText(value === null || value === undefined ? "" : String(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleChange = (event) => {
        const next = toDotDecimal(event.target.value, decimals);
        setText(next);
        if (onChange) onChange({ ...event, target: { ...event.target, value: next } });
    };

    return (
        <TextField
            {...rest}
            ref={ref}
            type="text"
            value={text}
            onChange={handleChange}
            inputProps={{ inputMode: "decimal", autoComplete: "off", ...inputProps }}
        />
    );
});

export default DecimalTextField;
